import React, { useContext, useEffect, useRef, useState } from "react";
import bulmaCalendar from "bulma-calendar";
import { GET } from "../../../../utils/fetchUtil";
import Box from "../../../../common/box/Box";
import Select from "../../../../common/select/Select";
import InputField from "../../../../common/input/InputField";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "../../../../common/theme/ThemeContext";
import { cleanJoin } from "../../../../utils/stringUtil";
import { MessageContext } from "../../../../common/message";
import "./FilterForm.scss";

function FilterForm({ settings, data, setData, setNumberOfRequests }) {
  const defaultFormState = {
    solver: "",
    department: "",
    submittedBy: "",
    requestType: "",
    requestStatus: "",
    subject: "",
  };

  const dateRef = useRef(null);
  const [calendars, setCalendars] = useState(null);
  const [formState, setFormState] = useState({ ...defaultFormState });
  const { t } = useTranslation();
  const theme = useContext(ThemeContext);
  const message = useContext(MessageContext);

  useEffect(() => {
    setCalendars(
      bulmaCalendar.attach('[type="date"]', {
        lang: "hr",
        color: theme.calendar,
        cancelLabel: "Odustani",
        clearLabel: "Poništi",
        todayLabel: "Danas",
        showTodayButton: false,
        isRange: true,
        displayMode: "inline",
        labelFrom: "Datum zaprimanja od",
        labelTo: "Datum zaprimanja do",
      })
    );

    dateRef.current.querySelector(".datetimepicker").style = "max-width: 100%";
  }, [theme]);

  const clear = (e) => {
    setFormState(defaultFormState);
    calendars[0].clear();
    e.currentTarget.blur();
  };

  const submit = (e) => {
    e.preventDefault();
    let query = Object.keys(formState)
      .map((key) => `${key}=${encodeURIComponent(formState[key])}`)
      .join("&");
    if (calendars[0].startDate && calendars[0].endDate) {
      query += `&receiptDateStart=${encodeURIComponent(calendars[0].startDate.getTime())}`;
      query += `&receiptDateEnd=${encodeURIComponent(calendars[0].endDate.getTime())}`;
    }
    GET(
      "/requests/submissions?" + query,
      (filtered) => {
        setData({ ...data, requests: filtered.requests });
        setNumberOfRequests(filtered.requests.length);
      },
      (error) => {
        message.toggle({
          title: t("requests.reviewDocuments.action.save.error"),
          content: error,
          hideNo: true,
          yesLabel: t("modalMessage.okLabel"),
          onYes: () => {
            message.toggle({});
          },
        });
      }
    );
  };

  // Field "subject" and "submittedBy" are textBox, text is allowed. Others are select boxes,
  // so they are limited to numbers.
  const onFieldChangeHandler = (property) => (e) => {
    if (property === "subject" || property === "submittedBy") {
      formState[property] = e.currentTarget.value;
      setFormState({ ...formState });
    } else {
      if (isNaN(e.currentTarget.value)) {
        formState[property] = "";
        setFormState({ ...formState });
      } else {
        formState[property] = e.currentTarget.value;
        setFormState({ ...formState });
      }
    }
  };

  return (
    <Box defaultPadding={0} isShadowless={false} isBorderless={true}>
      <div className={"subtitle is-size-4"}>Filter</div>

      <form onSubmit={submit}>
        <div className="field">
          <div className="control select">
            <Select
              value={formState.department}
              label={t("requests.allRequests.filterForm.department")}
              options={settings.departments}
              onChange={onFieldChangeHandler("department")}
            />
          </div>
        </div>

        <div className="field">
          <div className="control">
            <Select
              value={formState.solver}
              label={t("requests.allRequests.filterForm.solver")}
              options={settings.solvers}
              onChange={onFieldChangeHandler("solver")}
            />
          </div>
        </div>

        <div className="field">
          <div className="control">
            <Select
              value={formState.requestType}
              label={t("requests.allRequests.filterForm.requestType")}
              options={settings.requestTypes}
              onChange={onFieldChangeHandler("requestType")}
            />
          </div>
        </div>

        <div className="field">
          <p className="control is-expanded">
            <InputField
              value={formState.submittedBy}
              placeholder={t("requests.allRequests.filterForm.submittedBy")}
              onChange={onFieldChangeHandler("submittedBy")}
            />
          </p>
        </div>

        <div className="field">
          <div className="control">
            <Select
              value={formState.requestStatus}
              label={t("requests.allRequests.filterForm.requestStatus")}
              options={settings.requestStatuses}
              onChange={onFieldChangeHandler("requestStatus")}
            />
          </div>
        </div>

        <div className="field">
          <p className="control is-expanded">
            <InputField
              value={formState.subject}
              placeholder={t("requests.allRequests.filterForm.subject")}
              onChange={onFieldChangeHandler("subject")}
            />
          </p>
        </div>

        <div className="field" ref={dateRef}>
          <p className="control is-expanded has-icons-left">
            <input className="input" type="date" />
          </p>
        </div>

        <div className="is-flex is-justify-content-center">
          <button
            type={"submit"}
            onClick={(e) => e.currentTarget.blur()}
            style={{ minWidth: 100 }}
            className={cleanJoin(
              "button",
              `has-background-${theme.calendar}`,
              `has-text-white`,
              `has-text-white-on-hover`,
              `has-opacity-71-on-hover`,
              `mr-2`
            )}
          >
            {t("requests.allRequests.filterForm.search")}
          </button>

          <button
            onClick={clear}
            style={{ minWidth: 100 }}
            className={cleanJoin(
              "button",
              `has-background-${theme.calendar}`,
              `has-text-white`,
              `has-text-white-on-hover`,
              `has-opacity-71-on-hover`
            )}
          >
            {t("requests.allRequests.filterForm.cancel")}
          </button>
        </div>
      </form>
    </Box>
  );
}
export default FilterForm;
